import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { AudioDescription, Dubbing, VoiceOver } from "../utils/audio";
import { Caption, Category, Subtitle } from "../utils/vtt";

interface HomeworkState {
  subtitles: Subtitle[];
  captions: Caption[];
  voiceOvers: VoiceOver[];
  dubbings: Dubbing[];
  audioDescriptions: AudioDescription[];
  categories: { [className: string]: Category };
  setSubtitles: (setSubtitlesFn: (subtitles: Subtitle[]) => Subtitle[]) => void;
  setCaptions: (setCaptionsFn: (captions: Caption[]) => Caption[]) => void;
  setVoiceOvers: (voiceOvers: VoiceOver[]) => void;
  setDubbings: (dubbings: Dubbing[]) => void;
  setAudioDescriptions: (audioDescriptions: AudioDescription[]) => void;
  setCategories: (categories: { [className: string]: Category }) => void;
  addCategory: (category: Category) => void;
  removeCategory: (categoryUUID: string) => void;
}

const useHomeworkState = create<HomeworkState>()(
  devtools(
    (set, get) => ({
      subtitles: [],
      captions: [],
      categories: {},
      voiceOvers: [],
      dubbings: [],
      audioDescriptions: [],
      setSubtitles: (setSubtitlesFn) =>
        set({ subtitles: setSubtitlesFn(get().subtitles) }),
      setCaptions: (setCaptionsFn) =>
        set({ captions: setCaptionsFn(get().captions) }),
      setVoiceOvers: (voiceOvers) => set({ voiceOvers }),
      setDubbings: (dubbings) => set({ dubbings }),
      setAudioDescriptions: (audioDescriptions) => set({ audioDescriptions }),
      setCategories: (categories) => set({ categories }),
      removeCategory: (categoryUUID) =>
        set((oldState) => ({
          ...oldState,
          categories: Object.values(oldState.categories).reduce(
            (p, c) =>
              c.uuid !== categoryUUID ? { ...p, [c.uuid ?? ""]: c } : { ...p },
            {},
          ),
        })),
      addCategory: (category) =>
        set((oldState) => ({
          ...oldState,
          categories: {
            ...oldState.categories,
            [category.uuid ?? ""]: category,
          },
        })),
    }),
    {
      name: "homework-state",
    },
  ),
);

export default useHomeworkState;
